import {
	Button,
	ButtonGroup,
	Card,
	Heading,
	IconButton,
	LuIcon,
	Menu,
	Separator,
	Text,
	toast,
} from "@overmap-ai/blocks"
import {
	EMPTY_ARRAY,
	ERROR_MSG_UNEXPECTED,
	pluralize,
	type ProcedureInitiative,
	type Stored,
	submittedAtModelSortFn,
	toFileNameSafeString,
} from "@overmap-ai/common"
import {
	APIError,
	selectAssetsOfProject,
	selectLatestProcedureTypeFieldsOfProcedureType,
	selectProcedureInitiativesOfProcedureType,
	selectProcedureStepsOfProcedureType,
	selectProcedureTypeById,
	selectUsersOfProject,
} from "@overmap-ai/core"
import { useOpenState, useSort } from "@overmap-ai/hooks"
import { getRouteApi, Link, useLocation, useNavigate } from "@tanstack/react-router"
import type { InitialTableState, SortingState, Updater, VisibilityState } from "@tanstack/react-table"
import { cx } from "class-variance-authority"
import { saveAs } from "file-saver"
import Papa from "papaparse"
import { memo, useCallback, useMemo } from "react"

import { ProcedureInitiativeCard } from "../../components/Cards/ProcedureInitiativeCard"
import { ViewContainer } from "../../components/Containers/ViewContainer"
import { ProcedurePdfExportOptionsDialog } from "../../components/Dialogs/ProcedurePdfExportOptionsDialog"
import { ProcedureBulkDropdownMenu } from "../../components/DropdownMenus/ProcedureBulkDropdownMenu"
import { ProcedureDataFilterMenu } from "../../components/FilterMenus/ProcedureDataFilterMenu"
import { useDeserializeFieldSchema } from "../../components/Form/hooks"
import { ProcedureTypeIcon } from "../../components/Icons/ProcedureTypeIcon"
import { useProcedureTable, useProcedureTableColumns } from "../../components/Tables"
import { ColumnVisibilityDropdown, PaginationControl } from "../../components/Tables/components"
import { defaultRowsPerPageOptions } from "../../components/Tables/constants"
import { tableIconMapping } from "../../components/Tables/mappings"
import { Table } from "../../components/Tables/Table"
import { getProcedureDataCsvExporter } from "../../csv/CSVExporter/procedures"
import { interactiveCardCva } from "../../cva"
import { FilterRenderer } from "../../filtering/components"
import type { AnyFilter } from "../../filtering/filter"
import { useFilterManager, useFilters } from "../../filtering/hooks"
import { getProcedureDataFilterSchema } from "../../filtering/schemas/ProcedureDataFilterSchema"
import { useFieldSchemasAndConditionsOfProcedureSteps, useProcedureData } from "../../hooks/procedures"
import { useConfirmDeleteDialog } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import {
	setProcedureTypeDataColumnSorting,
	setProcedureTypeDataColumnVisibility,
	setProcedureTypeDataFilters,
	useAppDispatch,
	useAppSelector,
	useShallowEqaulSelector,
} from "../../store"
import {
	selectColumnSortingOfProcedureTypeData,
	selectColumnVisibilityOfProcedureTypeData,
	selectProceduresOfProjectAndProcedureType,
	selectProcedureTypeDataFiltersOfProcedureType,
} from "../../store/selectors"
import type { ProcedureData } from "../../typings"
import type { ProcedurePdfExportOptions } from "../../typings/reports"

const rowsPerPageOptions = [25, ...defaultRowsPerPageOptions]

const initialTableState: InitialTableState = {
	sorting: [
		{
			id: "submitted_at",
			desc: true,
		},
	],
	pagination: {
		pageSize: rowsPerPageOptions[0],
	},
}

const routeApi = getRouteApi(
	"/_loggedIn/$organizationId/_map/_layout/$projectId/_projectMap/procedure-types/$procedureTypeId",
)

export const ProcedureTypeDetailView = memo(() => {
	const { procedureTypeId, projectId } = routeApi.useParams()

	const procedureType = useAppSelector(selectProcedureTypeById(procedureTypeId))

	if (!procedureType) {
		throw new Error(`Procedure type ${procedureTypeId} not found`)
	}

	const dispatch = useAppDispatch()
	const procedureInitiatives = useShallowEqaulSelector(selectProcedureInitiativesOfProcedureType(procedureType.uuid))
	const navigate = useNavigate()
	const location = useLocation()
	const procedureSteps = useShallowEqaulSelector(selectProcedureStepsOfProcedureType(procedureType.uuid))
	const procedureTypeFields = useAppSelector(selectLatestProcedureTypeFieldsOfProcedureType(procedureType.uuid))
	const serializedFilters = useAppSelector(selectProcedureTypeDataFiltersOfProcedureType(procedureType.uuid))
	const columnSorting = useAppSelector(selectColumnSortingOfProcedureTypeData(procedureType.uuid))
	const columnVisibility = useAppSelector(selectColumnVisibilityOfProcedureTypeData(procedureType.uuid))
	const assets = useShallowEqaulSelector(selectAssetsOfProject(projectId))
	const users = useShallowEqaulSelector(selectUsersOfProject(projectId))
	const procedures = useShallowEqaulSelector(selectProceduresOfProjectAndProcedureType(projectId, procedureType.uuid))
	const sortedProcedures = useSort(procedures, submittedAtModelSortFn)
	const procedureData = useProcedureData(sortedProcedures)
	const openConfirmDeleteDialog = useConfirmDeleteDialog()

	const sortedProcedureInitiatives = useSort(procedureInitiatives, submittedAtModelSortFn)

	const assetsById = useMemo(() => {
		return Object.fromEntries(assets.map((asset) => [asset.uuid, asset]))
	}, [assets])

	const procedureTypeFieldSchema = useDeserializeFieldSchema(procedureTypeFields?.fields ?? EMPTY_ARRAY)

	const procedureStepFieldSchemasAndConditions = useFieldSchemasAndConditionsOfProcedureSteps(procedureSteps)

	const procedureDataFilterSchema = useMemo(() => {
		return getProcedureDataFilterSchema({
			procedureInitiatives,
			procedureTypeFieldSchema,
			procedureSteps,
			procedureStepFieldSchemasAndConditions,
			users,
		})
	}, [procedureInitiatives, procedureTypeFieldSchema, procedureSteps, procedureStepFieldSchemasAndConditions, users])

	const filters = useMemo(() => {
		return procedureDataFilterSchema.deserializeFilters(serializedFilters)
	}, [procedureDataFilterSchema, serializedFilters])

	const handleFiltersChange = useCallback(
		(filters: AnyFilter[]) => {
			dispatch(
				setProcedureTypeDataFilters({
					id: procedureType.uuid,
					filters: procedureDataFilterSchema.serializeFilters(filters),
				}),
			)
		},
		[dispatch, procedureDataFilterSchema, procedureType.uuid],
	)

	const handleColumnVisibilityChange = useCallback(
		(updater: Updater<VisibilityState>) => {
			dispatch(
				setProcedureTypeDataColumnVisibility({
					id: procedureType.uuid,
					columnVisibility: typeof updater === "function" ? updater(columnVisibility) : updater,
				}),
			)
		},
		[dispatch, procedureType.uuid, columnVisibility],
	)

	const handleSortingChange = useCallback(
		(updater: Updater<SortingState>) => {
			dispatch(
				setProcedureTypeDataColumnSorting({
					id: procedureType.uuid,
					columnSorting: typeof updater === "function" ? updater(columnSorting) : updater,
				}),
			)
		},
		[dispatch, procedureType.uuid, columnSorting],
	)

	const procedureDataFilterManager = useFilterManager({
		filterSchema: procedureDataFilterSchema,
		filters,
		onFilterChange: handleFiltersChange,
	})

	const filteredProcedureData = useFilters({
		filterManager: procedureDataFilterManager,
		filters,
		data: procedureData,
	})

	const columns = useProcedureTableColumns({
		procedureType,
		procedureTypeFieldSchema,
		procedureSteps,
		procedureStepFieldSchemasAndConditions,
		assetsById,
	})

	const table = useProcedureTable({
		columns,
		data: filteredProcedureData,
		initialState: initialTableState,
		state: {
			columnVisibility,
			sorting: columnSorting,
		},
		onColumnVisibilityChange: handleColumnVisibilityChange,
		onSortingChange: handleSortingChange,
	})

	const handleRowClick = useCallback(
		({ procedure }: ProcedureData) => {
			void navigate({
				from: "/$organizationId/$projectId/procedure-types/$procedureTypeId",
				to: "/$organizationId/$projectId/procedures/$procedureId",
				params: (prev) => ({
					...prev,
					procedureId: procedure.uuid,
				}),
			})
		},
		[navigate],
	)

	const handleEdit = useCallback(() => {
		void navigate({
			from: "/$organizationId/$projectId/procedure-types/$procedureTypeId",
			to: "/$organizationId/settings/procedure-types/$procedureTypeId",
		})
	}, [navigate])

	const handleProcedureInitiativeCardClick = useCallback(
		(procedureInitiative: Stored<ProcedureInitiative>) => {
			void navigate({
				from: "/$organizationId/$projectId/procedure-types/$procedureTypeId",
				to: "/$organizationId/$projectId/initiatives/$procedureInitiativeId",
				params: (prev) => ({
					...prev,
					procedureInitiativeId: procedureInitiative.uuid,
				}),
			})
		},
		[navigate],
	)

	const handleViewProcedureInitiative = useCallback(
		(procedureInitiative: Stored<ProcedureInitiative>) => {
			void navigate({
				from: "/$organizationId/$projectId/procedure-types/$procedureTypeId",
				to: "/$organizationId/$projectId/initiatives/$procedureInitiativeId",
				params: (prev) => ({ ...prev, procedureInitiativeId: procedureInitiative.uuid }),
			})
		},
		[navigate],
	)

	const handleDeleteProcedureInitiative = useCallback(
		(procedureInitiative: Stored<ProcedureInitiative>) => {
			openConfirmDeleteDialog({
				title: "Delete initiave",
				description: "Are you sure you want to delete this initiave? This action cannot be undone.",
				onAction: () => {
					void sdk.procedureInitiatives.delete(procedureInitiative.uuid)
				},
			})
		},
		[openConfirmDeleteDialog],
	)

	const getSelectedRows = useCallback(() => table.getSelectedRowModel().rows.map((row) => row.id), [table])

	const handleExportToCsv = useCallback(() => {
		const csvExporter = getProcedureDataCsvExporter(
			table.getSortedRowModel().rows.map((row) => row.original),
			{
				procedureType: procedureType,
				assets: assets,
				procedureSteps: procedureSteps,
				procedureInitiatives: procedureInitiatives,
				procedureTypeFieldSchema: procedureTypeFieldSchema,
				procedureStepFieldSchemas: procedureStepFieldSchemasAndConditions,
			},
		)
		const csvData = Papa.unparse(csvExporter.toPapaUnparseFormat(), { quotes: true })
		const blob = new Blob([csvData], { type: "text/csv" })
		saveAs(blob, toFileNameSafeString(procedureType.name, ".csv"))
	}, [
		table,
		procedureType,
		assets,
		procedureSteps,
		procedureInitiatives,
		procedureTypeFieldSchema,
		procedureStepFieldSchemasAndConditions,
	])

	const [showPdfExportOptionsDialog, openPdfExportOptionsDialog, closePdfExportOptionsDialog] = useOpenState(false)

	const handleExportToPdf = useCallback(
		(options: ProcedurePdfExportOptions) => {
			const selectedRows = table.getSelectedRowModel().rows.map((row) => row.original.procedure.uuid)

			void sdk.procedures
				.getProceduresPdfReport(procedureType.uuid, selectedRows, options)
				.then(() => {
					toast.success({
						title: "Report requested",
						description: `Requested report for ${selectedRows.length} ${pluralize("procedure", selectedRows.length)}. Visit the Reports page when it is ready.`,
					})
				})
				.catch((e) => {
					toast.error({
						title: "Failed to generate report",
						description: e instanceof APIError ? e.message : ERROR_MSG_UNEXPECTED,
					})
				})
		},
		[table, procedureType.uuid],
	)

	const handleBulkDelete = useCallback(() => {
		const procedureIds = getSelectedRows()

		openConfirmDeleteDialog({
			title: "Delete procedures",
			description: `The ${procedureIds.length} ${pluralize("procedure", procedureIds.length)} will be deleted. This action cannot be undone.`,
			onAction: () => void sdk.procedures.bulkDelete(procedureIds),
		})

		table.resetRowSelection()
	}, [table, getSelectedRows, openConfirmDeleteDialog])

	return (
		<ViewContainer>
			<div className="flex size-full flex-col gap-4 overflow-hidden">
				<div className="flex w-full items-center gap-2">
					<Heading className="flex min-w-0 grow items-center gap-2" size="xl">
						<ProcedureTypeIcon procedureTypeId={procedureType.uuid} />
						<span className="truncate">{procedureType.name}</span>
					</Heading>

					<Menu.Root>
						<Menu.ClickTrigger>
							<IconButton accentColor="base" size="sm" variant="ghost">
								<LuIcon icon={modelIconMapping.more} />
							</IconButton>
						</Menu.ClickTrigger>
						<Menu.Content size="sm">
							<Menu.Item onSelect={handleEdit}>
								<LuIcon icon={modelIconMapping.edit} />
								Edit
							</Menu.Item>
						</Menu.Content>
					</Menu.Root>
				</div>

				<Separator size="full" />

				<div className="flex size-full flex-col gap-4 overflow-y-auto overflow-x-hidden">
					<Card className="flex shrink-0 flex-col gap-2" size="sm">
						<div className="flex items-center justify-between">
							<Heading>Initiatives</Heading>

							<Link
								from="/$organizationId/$projectId/procedure-types/$procedureTypeId"
								to="/$organizationId/$projectId/initiatives/create"
								search={{ redirect: location.href, procedure_type: procedureType.uuid }}
							>
								<Button variant="surface" size="sm">
									<LuIcon icon={modelIconMapping.add} />
									Create
								</Button>
							</Link>
						</div>

						<div className="flex w-full shrink-0 scroll-px-4 gap-4 overflow-x-scroll">
							{sortedProcedureInitiatives.map((procedureInitiative) => (
								<ProcedureInitiativeCard
									key={procedureInitiative.uuid}
									variant="surface"
									onClick={handleProcedureInitiativeCardClick}
									onView={handleViewProcedureInitiative}
									onDelete={handleDeleteProcedureInitiative}
									procedureInitiative={procedureInitiative}
									className={cx(interactiveCardCva(), "w-xs h-64 shrink-0 cursor-pointer")}
									size="sm"
								/>
							))}
						</div>
					</Card>

					<Card className="flex flex-col gap-2" size="sm">
						<ButtonGroup
							className="flex w-full justify-between gap-2"
							size="xs"
							variant="soft"
							accentColor="base"
						>
							<div className="flex items-center gap-2">
								<Text>Procedures</Text>
								<ProcedureDataFilterMenu
									filterManager={procedureDataFilterManager}
									procedureInitiatives={procedureInitiatives}
									procedureTypeFieldSchema={procedureTypeFieldSchema}
									procedureSteps={procedureSteps}
									procedureStepFieldSchemasAndConditions={procedureStepFieldSchemasAndConditions}
									users={users}
									align="start"
									size="sm"
								>
									<Button>
										<LuIcon icon={modelIconMapping.filter} />
										Filter
									</Button>
								</ProcedureDataFilterMenu>

								{procedureDataFilterManager.getFilters().length > 0 && (
									<IconButton onClick={procedureDataFilterManager.removeAll}>
										<LuIcon icon={modelIconMapping.close} />
									</IconButton>
								)}
							</div>

							<div className="flex gap-2">
								{getSelectedRows().length > 0 ? (
									<ProcedureBulkDropdownMenu
										onExportCsv={handleExportToCsv}
										onExportPdf={openPdfExportOptionsDialog}
										onDelete={handleBulkDelete}
										size="sm"
									>
										<Button>
											<LuIcon icon={modelIconMapping.command} />
											Actions
										</Button>
									</ProcedureBulkDropdownMenu>
								) : null}

								<ColumnVisibilityDropdown table={{ ...table }} align="end" size="sm">
									<IconButton>
										<LuIcon icon={tableIconMapping.columns} />
									</IconButton>
								</ColumnVisibilityDropdown>
							</div>
						</ButtonGroup>

						<FilterRenderer filterManager={procedureDataFilterManager} size="sm" />

						<div className="max-h-full w-full overflow-auto">
							<Table
								className="w-full"
								size="sm"
								variant="surface"
								table={{ ...table }}
								onRowClick={handleRowClick}
								compact
								interactive
								emptyText="No procedures."
							/>
						</div>

						<PaginationControl table={{ ...table }} rowPerPageOptions={rowsPerPageOptions} />
					</Card>
				</div>
			</div>
			<ProcedurePdfExportOptionsDialog
				key={procedureType.uuid}
				procedureTypeId={procedureType.uuid}
				onSubmitOptions={handleExportToPdf}
				open={showPdfExportOptionsDialog}
				onOpenChange={closePdfExportOptionsDialog}
			/>
		</ViewContainer>
	)
})
ProcedureTypeDetailView.displayName = "ProcedureTypeDetailView"
